import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ModalBottomButtonContainer } from '../modals/Styles';
import { PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import { DocumentCarouselContext } from './DocumentProvider';
import { useFormContext } from 'react-hook-form';
import { ReportType, ReportOutputType } from '../../graphql/schema/p4types';

const DocumentCarouselButtons = ({ onPrint }: { onPrint: () => void }) => {
  const { onSkip, onBack, prevDocument, nextDocument } = useContext(DocumentCarouselContext);

  const { watch } = useFormContext();
  const [disabled, setDisabled] = useState<boolean>(false);

  let printAction = 'Send/Print';
  let printIconName = 'send';
  if (watch('overrideOutput')) {
    switch (watch('outputType')) {
      case ReportOutputType.Email:
        printAction = 'Send';
        printIconName = 'mail';
        break;
      default:
        printAction = 'Print';
        printIconName = 'print';
    }
  }

  let backText = 'Previous';
  if (prevDocument) {
    switch (prevDocument.reportType) {
      case ReportType.SalesAcknowledgement:
        backText += ' (Sales Acknowledgement)';
        break;
      case ReportType.PickingInstruction:
        backText += ' (Picking Instruction)';
        break;
    }
  }

  let skipText = 'Skip';
  if (nextDocument) {
    switch (nextDocument.reportType) {
      case ReportType.SalesAcknowledgement:
        skipText += ' (To Sales Acknowledgement)';
        break;
      case ReportType.PickingInstruction:
        skipText += ' (To Picking Instruction)';
        break;
    }
  }
  const disabledTimeout = useRef(null);

  const temporaryDisable = () => {
    setDisabled(true);
    disabledTimeout.current = setTimeout(() => setDisabled(false), 2000);
  };

  useEffect(() => {
    return () => {
      if (disabledTimeout.current) {
        clearTimeout(disabledTimeout.current);
      }
    };
  }, []);

  //Click wrappers to avoid passing the event through, onBack/onSkip do not expect an event argument.
  const onClickBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const onClickSkip = useCallback(() => {
    onSkip(false);
  }, [onSkip]);

  return (
    <ModalBottomButtonContainer>
      {onBack ? (
        <SecondaryButton
          disabled={disabled}
          onClick={() => {
            temporaryDisable();
            onClickBack();
          }}
          data-testid={'document-carousel-back'}
        >
          {backText}
        </SecondaryButton>
      ) : null}
      <SecondaryButton
        disabled={disabled}
        onClick={() => {
          temporaryDisable();
          onClickSkip();
        }}
        data-testid={'document-carousel-skip'}
      >
        {skipText}
      </SecondaryButton>
      <PrimaryButton
        onClick={() => {
          temporaryDisable();
          onPrint();
        }}
        iconProps={{
          iconName: printIconName,
        }}
        data-testid={'document-carousel-print-send'}
        disabled={disabled}
      >
        {printAction}
      </PrimaryButton>
    </ModalBottomButtonContainer>
  );
};

export default DocumentCarouselButtons;
